import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import FormData from "form-data";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getOrderRate } from "../../redux/actions/action-creator";
import ReviewPopupFrame from "./ReviewPopupFrame";
import './OrderReviewModal.css';

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getRatingsPayload = (ordersDetail) => {
  const ratings = parseJsonValue(ordersDetail?.ratings);

  if (isPlainObject(ratings)) return ratings;

  if (Array.isArray(ratings)) {
    return ratings.map(parseJsonValue).find(isPlainObject) || null;
  }

  return null;
};

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    const matchedKey = keys.find((key) => hasValue(source?.[key]));
    if (matchedKey) return source[matchedKey];
  }

  return fallback;
};

const OrderReviewModal = ({ show, onClose, onSubmitSuccess, ordersDetail }) => {
  const dispatch = useDispatch();
  const [ratings, setRatings] = useState({
    quality: 0,
    service: 0,
    packaging: 0,
    accuracy: 0
  });

  const [hover, setHover] = useState({
    quality: 0,
    service: 0,
    packaging: 0,
    accuracy: 0
  });
  
  const [feedbackType, setFeedbackType] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const maxWords = 200;
  const feedbackTypes = Array.isArray(ordersDetail?.feedback_types)
    ? ordersDetail.feedback_types
    : [];

  const ratingCategories = [
    { key: 'quality', label: 'Quality of products delivered' },
    { key: 'service', label: 'Delivered service' },
    { key: 'packaging', label: 'Packaging goods' },
    { key: 'accuracy', label: 'Products Accuracy' }
  ];
  useEffect(() => {
    if (ordersDetail) {
      const nextFeedbackTypes = Array.isArray(ordersDetail?.feedback_types)
        ? ordersDetail.feedback_types
        : [];
      const ratingsPayload = getRatingsPayload(ordersDetail);
      const ratingSources = [ratingsPayload, ordersDetail].filter(isPlainObject);

      setRatings({
        quality: getFirstValue(ratingSources, [
          "product_quality_rating",
          "product_quality_points",
          "productQualityRating",
          "productQualityPoints",
          "quality",
        ], 0),
        service: getFirstValue(ratingSources, [
          "rating",
          "user_rating_points",
          "userRatingPoints",
          "service",
        ], 0),
        packaging: getFirstValue(ratingSources, [
          "packaging_goods_rating",
          "packaging_goods_points",
          "packagingGoodsRating",
          "packagingGoodsPoints",
          "packaging",
        ], 0),
        accuracy: getFirstValue(ratingSources, [
          "product_accuracy_rating",
          "product_accuracy_points",
          "productAccuracyRating",
          "productAccuracyPoints",
          "accuracy",
        ], 0)
      });

      setComment(getFirstValue(ratingSources, [
        "comment",
        "user_comment",
        "userComment",
      ], ""));
      setFeedbackType(
        getFirstValue(ratingSources, [
          "feedback_id",
          "feedback_type",
          "feedback_type_id",
          "feedbackType",
        ]) ||
        nextFeedbackTypes[0]?.id ||
        ''
      );
    }
  }, [ordersDetail]);

  useEffect(() => {
    if (!show) {
      setSubmitting(false);
      setImages((prevImages) => {
        prevImages.forEach((image) => {
          if (image.preview) {
            URL.revokeObjectURL(image.preview);
          }
        });
        return [];
      });
    }
  }, [show]);

  const handleRating = (key, value) => {
    setRatings(prev => ({ ...prev, [key]: value }));
  };

  const handleHover = (key, value) => {
    setHover(prev => ({ ...prev, [key]: value }));
  };
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages(prev => {
      const removedImage = prev[index];
      if (removedImage?.preview) {
        URL.revokeObjectURL(removedImage.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const wordCount = comment.trim().split(/\s+/).filter(word => word.length > 0).length;

  const isSuccessResponse = (response) =>
    response?.result === true ||
    response?.result === 1 ||
    response?.result === "1" ||
    response?.success === true ||
    response?.success === 1 ||
    response?.success === "1";

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const orderId =
      ordersDetail?.order_id ||
      ordersDetail?.booking_order_id ||
      ordersDetail?.bookingOrderId ||
      ordersDetail?.id;

    if (!orderId) {
      toast.error("Order id not found.");
      return;
    }

    const formData = new FormData();
    formData.append("booking_order_id", orderId);
    formData.append("product_quality_rating", ratings.quality);
    formData.append("rating", ratings.service);
    formData.append("packaging_goods_rating", ratings.packaging);
    formData.append("product_accuracy_rating", ratings.accuracy);
    formData.append("comment", comment.trim());
    formData.append("feedback_id", feedbackType || "");
    formData.append("segment_slug", ordersDetail?.segment_slug || "");

    const submittedRatingData = {
      product_quality_rating: ratings.quality,
      rating: ratings.service,
      packaging_goods_rating: ratings.packaging,
      product_accuracy_rating: ratings.accuracy,
      comment: comment.trim(),
      feedback_id: feedbackType || "",
    };

    images.forEach((image) => {
      if (image.file) {
        formData.append("rating_image_one", image.file);
      }
    });

    setSubmitting(true);

    try {
      const response = await dispatch(getOrderRate(formData));

      if (isSuccessResponse(response)) {
        toast.success(response?.message || "Review submitted successfully.");
        onSubmitSuccess && onSubmitSuccess({
          response,
          ratingData: submittedRatingData,
        });
        onClose();
        return;
      }

      toast.error(response?.message || "Unable to submit review.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to submit review."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  //const qualityRating = hover || rating;
  
  return (
    <>
      <ReviewPopupFrame title="Rate your order" onClose={handleClose} busy={submitting}>
          {/* Body */}
          <div className="review-popup-body">
            {/* Star Rating */}
            {ratingCategories.map((item) => {
              const activeRating = hover[item.key] || ratings[item.key];

              return (
                <div className="review-rating-group" key={item.key}>
                  
                  <div className="review-rating-row">
                    <label className="review-label">{item.label}</label>

                    <div className="review-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`review-star ${star <= activeRating ? 'active-start' : ''}`}
                          onClick={() => handleRating(item.key, star)}
                          onMouseEnter={() => handleHover(item.key, star)}
                          onMouseLeave={() => handleHover(item.key, 0)}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>

                  <span
                    className="review-rating-label"
                  >
                    {activeRating > 0 ? RATING_LABELS[activeRating] : ''}
                  </span>
                </div>
              );
            })}

            <select
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              className="review-select review-input-group"
            >
              {/*<option value="">Select Feedback</option>*/}
              {feedbackTypes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.feedback_type}
                </option>
              ))}
            </select>

            {/* Review Textarea */}
            <div className="review-input-group">
              <label className="review-label">Add comment if any</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Type your comment"
                className="review-textarea"
                rows="3"
              />
              <div className="review-word-count">
                <span className={wordCount > maxWords ? 'review-word-count-over' : ''}>
                  ~{wordCount > maxWords ? maxWords : wordCount} Words
                </span>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="review-upload-section">
              <label className="review-label">Upload Images (Optional)</label>
              
              <div className="review-images-container">
                <div className="review-images-grid">
                  {images.map((img, index) => (
                    <div key={index} className="review-image-item">
                      <img src={img.preview} alt={`Upload ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="review-image-remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  <label className="review-upload-box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
              
            </div>
          </div>

          {/* Footer */}
          <div className="review-popup-footer">
            <button className="review-btn review-btn-cancel" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button className="review-btn review-btn-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
      </ReviewPopupFrame>
      <ToastContainer position="top-right" autoClose={2500} />
    </>
  );
};

export default OrderReviewModal;
